/**
 * Shared font tokens for the app shell / new layout.
 * Mirror the structure of appThemeColors — import these instead of hardcoding families/sizes.
 */
export const appThemeFonts = {
  family: {
    /** App-wide font — IBM Plex Sans only. */
    ui: "'IBM Plex Sans', sans-serif",
    /** Same as ui (kept for existing call sites). */
    numeric: "'IBM Plex Sans', sans-serif",
    /** Same as ui (kept for existing call sites / MUI theme). */
    app: "'IBM Plex Sans', sans-serif",
  },

  size: {
    xxs: "5px",
    micro: "8px",
    xs: "10px",
    xsPlus: "10.5px",
    sm: "11px",
    smPlus: "11.5px",
    md: "12px",
    mdPlus: "12.5px",
    base: "13px",
    basePlus: "13.5px",
    lg: "14px",
    lgPlus: "15px",
    xl: "16px",
    xxl: "18px",
    display: "24px",
  },

  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  letterSpacing: {
    section: "0.06em",
    label: "0.05em",
  },

  lineHeight: {
    tight: 1,
    snug: 1.3,
    relaxed: 1.6,
  },
};

/** Ready-to-spread typography presets for sx / style objects. */
export const appTypography = {
  ui: {
    fontFamily: appThemeFonts.family.ui,
    fontSize: appThemeFonts.size.basePlus,
    fontStyle: "normal",
    fontWeight: appThemeFonts.weight.regular,
    fontStretch: "100%",
  },

  uiBase: {
    fontFamily: appThemeFonts.family.ui,
    fontSize: appThemeFonts.size.base,
    fontWeight: appThemeFonts.weight.regular,
  },

  uiSub: {
    fontFamily: appThemeFonts.family.ui,
    fontSize: appThemeFonts.size.mdPlus,
    fontWeight: appThemeFonts.weight.regular,
  },

  menuItem: {
    fontFamily: appThemeFonts.family.ui,
    fontSize: appThemeFonts.size.base,
    fontWeight: appThemeFonts.weight.regular,
  },

  menuTitle: {
    fontFamily: appThemeFonts.family.ui,
    fontSize: appThemeFonts.size.lg,
    fontWeight: appThemeFonts.weight.bold,
  },

  sectionLabel: {
    fontFamily: appThemeFonts.family.ui,
    fontSize: appThemeFonts.size.xs,
    fontWeight: appThemeFonts.weight.semibold,
    letterSpacing: appThemeFonts.letterSpacing.section,
    textTransform: "uppercase",
  },

  helpSectionLabel: {
    fontFamily: appThemeFonts.family.ui,
    fontSize: appThemeFonts.size.sm,
    fontWeight: appThemeFonts.weight.semibold,
    letterSpacing: appThemeFonts.letterSpacing.label,
    textTransform: "uppercase",
  },

  numeric: {
    fontFamily: appThemeFonts.family.numeric,
  },

  badge: {
    fontFamily: appThemeFonts.family.numeric,
    fontSize: appThemeFonts.size.xsPlus,
    fontWeight: appThemeFonts.weight.bold,
    lineHeight: appThemeFonts.lineHeight.tight,
  },

  initials: {
    fontFamily: appThemeFonts.family.numeric,
    fontSize: appThemeFonts.size.md,
    fontWeight: appThemeFonts.weight.bold,
  },

  clock: {
    fontFamily: appThemeFonts.family.numeric,
    fontSize: appThemeFonts.size.base,
    lineHeight: appThemeFonts.lineHeight.tight,
  },
};

export const appThemeFontCssVars = {
  "--app-font-ui": appThemeFonts.family.ui,
  "--app-font-numeric": appThemeFonts.family.numeric,
  "--app-font-app": appThemeFonts.family.app,

  "--app-font-size-xxs": appThemeFonts.size.xxs,
  "--app-font-size-micro": appThemeFonts.size.micro,
  "--app-font-size-xs": appThemeFonts.size.xs,
  "--app-font-size-sm": appThemeFonts.size.sm,
  "--app-font-size-md": appThemeFonts.size.md,
  "--app-font-size-md-plus": appThemeFonts.size.mdPlus,
  "--app-font-size-base": appThemeFonts.size.base,
  "--app-font-size-base-plus": appThemeFonts.size.basePlus,
  "--app-font-size-lg": appThemeFonts.size.lg,
  "--app-font-size-lg-plus": appThemeFonts.size.lgPlus,
  "--app-font-size-xl": appThemeFonts.size.xl,
  "--app-font-size-xxl": appThemeFonts.size.xxl,
  "--app-font-size-display": appThemeFonts.size.display,

  "--app-font-weight-regular": String(appThemeFonts.weight.regular),
  "--app-font-weight-medium": String(appThemeFonts.weight.medium),
  "--app-font-weight-semibold": String(appThemeFonts.weight.semibold),
  "--app-font-weight-bold": String(appThemeFonts.weight.bold),
};
